import { create } from "zustand";
import axiosInstance from "../config/axiosConfig";
import useConfigStore from "../store/configStore";


// New interface for session history response structure
export interface SessionHistoryItem {
  session_id: number;
  session_topic: string;
  session_created_at: string;
  session_count: number;
  chat_id: number;
  last_message: string;
  chat_type: string;
  timestamp: string;
  course_id?: number | string;
}

// Legacy interface for backward compatibility (if needed)
export interface ChatSession {
  _id: string;
  session_title: string;
  created_at: string; // ISO datetime string
  user_id: string;
  project_id: string;
  id: string;
  project_name: string;
  last_message: string;
  timestamp: string; // ISO datetime string
  last_message_id: string;
  index_id: number;
  total_sessions_count: number;
  updated_at: string | null;
  course_id?: number | string;
}

interface ChatSessionStore {
  chatSessions: ChatSession[];
  isLoading: boolean;
  error: string | null;
  setChatSessions: (sessions: ChatSession[]) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearSessions: () => void;
  fetchChatSessions: (search?: string) => Promise<void>;
  selectedSessionId: string | null; // Optional field for selected session
  sessionIsOn?: boolean; // Optional field to indicate if a session is selected
  setSessionIsOn: (isOn: boolean) => void; // Optional setter for sessionIsOn
  setSelectedSessionId: (id: string | null) => void; // Optional setter for selected session
  dropdownSelectedProjectId: string | null; // <-- NEW: for dropdown project selection
  setDropdownSelectedProjectId: (id: string | null) => void; // <-- NEW: setter
  setShowNavigationArrows: (show: boolean) => void; // Optional setter for showing navigation arrows
  showNavigationArrows: boolean; // Optional field to control navigation arrows visibility
  historySearch: string; // Optional field for search in history
  setHistorySearch: (search: string) => void; // Optional setter for history search
}

const useChatSessionStore = create<ChatSessionStore>((set) => ({
  chatSessions: [],
  isLoading: false,
  error: null,
  showNavigationArrows: false, // Optional field to control navigation arrows visibility
  setShowNavigationArrows: (show) => set({ showNavigationArrows: show }),
  sessionIsOn: false, // Optional field to indicate if a session is selected
  setChatSessions: (sessions) =>
    set((_state) => {
      // console.log(_state);
      return {
        chatSessions: sessions,
      };
    }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearSessions: () =>
    set({
      chatSessions: [],
      error: null,
      isLoading: false,
      selectedSessionId: null,
      dropdownSelectedProjectId: null,
      sessionIsOn: false, // Reset sessionIsOn when clearing sessions
    }),
  fetchChatSessions: async (search) => {
    set({ isLoading: true, error: null });
    const endpoint = useConfigStore.getState().getEndpoint('fetchSessions');
    try {
      const response = await axiosInstance.get(
        endpoint,
        {
          params: { search: search || undefined },
        }
      );
      
      // Handle new response structure
      if (response.data.entity_session_history) {
        // Convert new structure to legacy structure for compatibility
        const convertedSessions: ChatSession[] = response.data.entity_session_history.map((item: SessionHistoryItem) => ({
          _id: item.chat_id.toString(),
          session_title: item.session_topic,
          created_at: item.session_created_at,
          user_id: "", // Not available in new structure
          project_id: "", // Not available in new structure
          id: item.chat_id.toString(),
          project_name: item.course_id ? item.course_id.toString() : item.session_topic, // Use session_topic as project_name
          last_message: item.last_message,
          timestamp: item.timestamp,
          last_message_id: item.chat_id.toString(),
          index_id: item.session_id,
          total_sessions_count: item.session_count,
          updated_at: item.timestamp,
          course_id: item.course_id,
        }));
        
        set({
          chatSessions: convertedSessions,
          isLoading: false,
          error: null,
        });
      } else if (response.data.status === "success") {
        // Handle legacy response structure
        set({
          chatSessions: response.data.data,
          isLoading: false,
          error: null,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch sessions");
      }
    } catch (error: any) {
      set({
        isLoading: false,
        error: error.message || "Unknown error",
      });
    }
  },
  selectedSessionId: null, // Optional field for selected session
  setSelectedSessionId: (id) =>
    set((_state) => {
      // console.log(_state);
      return {
        selectedSessionId: id,
      };
    }),
  setSessionIsOn: (isOn) =>
    set((_state) => {
      // console.log(_state);
      return {
        sessionIsOn: isOn,
      };
    }),
  dropdownSelectedProjectId: null, // <-- NEW: for dropdown project selection
  setDropdownSelectedProjectId: (id) =>
    set(() => ({
      dropdownSelectedProjectId: id,
    })),
  historySearch: "", // Optional field for search in history
  setHistorySearch: (search) =>
    set((_state) => {
      // console.log(_state);
      return {    
      historySearch: search,
      };
    }),
}));

export default useChatSessionStore;
